import { FC, useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import strings from '../../../common/Translation/Translate';
import Notification from '../../../components/Notification';
import PageLoader from '../../../components/PageLoader';
import axiosInstance from '../../../utils/axios';
import { getTableAvatarSx } from '../../../utils/avatarColors';
import { privileges } from '../../../utils/constants/privileges';
import { URLS } from '../../../utils/constants/urls';
import ListPage from '../CommonPage';
import { formatLastUpdatedCell } from '../CommonPage/lastUpdatedColumn';
import UsersEntityDrawer from './UsersEntityDrawer';
import { PageProps } from '../../../utils/constants/data';
import dayjs from 'dayjs';
import { images } from '../../../utils/constants/images';

import {
  filterDataforApi,
  pageNo,
  rowCount,
  setFilter,
  setFilterDataForApi,
  setPageNo,
} from 'utils/redux/reducer/filter-slice';
import { Privilege } from 'utils/redux/reducer/authentication-slice';
import { AlertColor, Avatar, Box, Chip, Tooltip, Typography, useTheme } from '@mui/material';
import DrawerMenu from '../../../components/DrawerMenu';
import { LocationOn, Replay, Schedule } from '@mui/icons-material';

const getUserInitials = (row: any) => {
  const first = String(row?.firstName || '').trim();
  const last = String(row?.lastName || '').trim();
  if (first || last) {
    const initials = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
    if (initials) return initials;
  }
  return String(row?.fullName || '').trim().slice(0, 2).toUpperCase() || '?';
};

const formatRole = (role?: string | null) => {
  if (!role) return '—';
  return role.replace(/^ROLE_/, '').replace(/_/g, ' ');
};

const UserManagement: FC = () => {
  const dispatch = useDispatch();
  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);
  const IsPrivilege = useSelector(Privilege);
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const roleQuery = (searchParams.get('role') || '').trim();
  const [pageResponse, setPageResponse] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sessionsDrawerOpen, setSessionsDrawerOpen] = useState<boolean>(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [sessionsPageResponse, setSessionsPageResponse] = useState<any[]>([]);
  const [entityDrawerOpen, setEntityDrawerOpen] = useState<boolean>(false);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  useLayoutEffect(() => {
    if (!roleQuery) return;
    dispatch(setPageNo(0));
    dispatch(setFilter({ role: roleQuery }));
    dispatch(setFilterDataForApi([`role:cic:${roleQuery}`]));
  }, [dispatch, roleQuery]);

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error.response.data?.error_description ?? error.response.data.message ?? error.message,
    });
  };

  const handleOpenSessions = (userId: number) => {
    setSelectedUserId(userId);
    setSessionsDrawerOpen(true);
  };

  const handleCloseSessions = () => {
    setSessionsDrawerOpen(false);
    setSelectedUserId(null);
  };

  const fetchUserSessions = useCallback(
    async (filterData: PageProps) => {
      try {
        setIsLoading(true);
        const requestBody = filterData || {
          page: index + 1,
          size: pageSize,
          filter: filter,
          sort: ['checkIn:ASC'],
        };
        const { data } = await axiosInstance.post(URLS.getUserSessions(selectedUserId!), requestBody);
        const content = data?.content?.map((item: any) => {
          return {
            ...item,
            checkIn: dayjs(item.checkIn).format('HH:mm ddd, MMM DD, YYYY'),
            checkOut: item.checkOut ? dayjs(item.checkOut).format('HH:mm ddd, MMM DD, YYYY') : 'NA',
            location: item.mapUrl ? (
              <LocationOn
                sx={{ color: 'primary.main', cursor: 'pointer' }}
                onClick={() => window.open(item.mapUrl, '_blank')}
              />
            ) : (
              '-'
            ),
          };
        });
        setSessionsPageResponse({ ...data, content: content });
      } catch (error: any) {
        handleCatch(error);
      } finally {
        setIsLoading(false);
      }
    },
    [selectedUserId, index, pageSize, filter],
  );

  useEffect(() => {
    if (sessionsDrawerOpen && selectedUserId) {
      fetchUserSessions(null as any);
    }
  }, [fetchUserSessions, sessionsDrawerOpen, selectedUserId]);

  const handleCustomImageMethod = async (id: number) => {
    try {
      setIsLoading(true);
      const { status } = await axiosInstance.put(URLS.resetUser(id));
      if (status === 200) {
        setIsLoading(false);
        setMessage({
          display: true,
          severity: 'success',
          message: strings.password_reset_success_message,
        });
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleUser = async (id: number) => {
    try {
      setIsLoading(true);
      const { status } = await axiosInstance.put(URLS.toggleUser(id));
      if (status === 200) {
        setMessage({
          display: true,
          severity: 'success',
          message: strings.status_change_success_message,
        });
        await getListData(null);
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  };

  const openEntityDrawer = (id: number | null) => {
    setEditingUserId(id);
    setEntityDrawerOpen(true);
  };

  const closeEntityDrawer = () => {
    setEntityDrawerOpen(false);
    setEditingUserId(null);
  };

  const handleEntitySaved = () => {
    closeEntityDrawer();
    getListData(null);
  };

  const pageConfig = {
    title: strings.menu_text_users,
    subtitle: strings.users_list_subtitle,
    searchField: roleQuery ? 'role' : 'search',
    searchPlaceholder: roleQuery ? 'Filter by role' : 'Search users',
    initialSearchText: roleQuery,
    AddButtonText: strings.usermgmt_title_add,
    listApi: URLS.users(false),
    deleteApi: URLS.deleteUserById,
    addPrivilege: privileges.add_user,
    editPrivilege: privileges.edit_user,
    deletePrivilege: privileges.delete_user,
    customAdd: () => openEntityDrawer(null),
    customEdit: (id: number) => openEntityDrawer(id),
    customImage: [
      {
        materialIcon: <Schedule fontSize="small" sx={{ color: theme.palette.warning.main }} />,
        handleCustomImageMethod: (id: number) => handleOpenSessions(id),
        tooltip: 'View Sessions',
        buttonText: 'View Sessions',
        privilege: privileges.view_user,
      },
      {
        materialIcon: <Replay fontSize="small" sx={{ color: theme.palette.info.main }} />,
        handleCustomImageMethod: (id: number) => {
          void handleCustomImageMethod(id);
        },
        tooltip: strings.reset_password_title,
        buttonText: strings.reset_password_title,
        privilege: privileges.reset_password_user,
      },
    ],
    tableColumn: [
      {
        field: 'fullName',
        headerName: 'Name',
        flex: 2,
        sortable: true,
        renderCell: (_value: any, row: any) => {
          const name = row.fullName || `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim();
          return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={getTableAvatarSx(name)}>
              {getUserInitials(row)}
            </Avatar>
            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
              {name || '—'}
            </Typography>
          </Box>
          );
        },
      },
      {
        field: 'email',
        headerName: 'Contact',
        flex: 2,
        sortable: true,
        renderCell: (_value: any, row: any) => {
          const email = row.email;
          const phone = String(row.phone || '').replace(/\D/g, '');
          if (!email && !phone) return '—';
          return (
            <>
              {email ? <Typography sx={{ fontSize: '0.8125rem', lineHeight: 1.35 }}>{email}</Typography> : null}
              {phone ? (
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.35 }}>{phone}</Typography>
              ) : null}
            </>
          );
        },
      },
      {
        field: 'role',
        headerName: 'Role',
        flex: 1,
        sortable: true,
        renderCell: (value: any) =>
          value ? <Chip size="small" label={formatRole(value)} sx={{ fontWeight: 600 }} /> : '—',
      },
      {
        field: 'organizationCount',
        headerName: 'Access Scope',
        flex: 1.2,
        sortable: false,
        renderCell: (_value: any, row: any) => {
          const count = Number(row.organizationCount ?? 0);
          return (
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, color: '#344054', lineHeight: 1.35 }}>
              {count === 1 ? '1 Property' : `${count} Properties`}
            </Typography>
          );
        },
      },
      {
        field: 'lastLoginAt',
        headerName: 'Last Login',
        flex: 1.4,
        sortable: true,
        renderCell: (_value: any, row: any) => formatLastUpdatedCell(row.lastLoginAt),
      },
      {
        field: 'enabled',
        headerName: 'Status',
        flex: 1,
        sortable: false,
        renderCell: (_value: any, row: any) => {
          const canToggle = IsPrivilege?.includes(privileges.block_user);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip
                title={row?.enabled ? strings.usermgmt_button_active_user : strings.usermgmt_button_in_active_user}>
                <Box
                  component="img"
                  src={row?.enabled ? images.activeToggleIcon : images.inActiveToggleIcon}
                  alt="status icon"
                  sx={{
                    width: 18,
                    height: 18,
                    cursor: canToggle ? 'pointer' : 'default',
                    opacity: canToggle ? 1 : 0.5,
                  }}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (canToggle) {
                      void toggleUser(row.id);
                    }
                  }}
                />
              </Tooltip>
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#344054' }}>
                {row?.enabled ? 'Active' : 'Inactive'}
              </Typography>
            </Box>
          );
        },
      },
    ],
  };

  const getListData = useCallback(
    async (filterData: any) => {
      try {
        setIsLoading(true);
        const body = filterData ?? { page: index + 1, size: pageSize, filter: filter, sort: ['createdAt:desc'] };
        const { data } = await axiosInstance.post(pageConfig.listApi, body);
        setPageResponse(data);
      } catch (error: any) {
        handleCatch(error);
      } finally {
        setIsLoading(false);
      }
    },
    [pageConfig.listApi, index, pageSize, filter],
  );

  useEffect(() => {
    (async () => {
      await getListData(null);
    })();
  }, [getListData]);

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  return (
    <>
      {isLoading && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <ListPage pageConfig={pageConfig} pageResponse={pageResponse} getList={getListData} page="user" />

      <UsersEntityDrawer
        open={entityDrawerOpen}
        editingId={editingUserId}
        onClose={closeEntityDrawer}
        onSaved={handleEntitySaved}
      />

      <DrawerMenu
        width="50%"
        open={sessionsDrawerOpen}
        handleClose={handleCloseSessions}
        closeButton={true}
        title="User Sessions">
        <ListPage
          pageConfig={{
            title: '',
            listApi: URLS.getUserSessions(selectedUserId!),
            tableColumn: [
              { field: 'checkIn', headerName: 'Check-In', width: 150 },
              { field: 'checkOut', headerName: 'Check-Out', width: 150 },
              { field: 'location', headerName: 'Location', width: 150 },
            ],
            hideActionText: true,
          }}
          pageResponse={sessionsPageResponse}
          getList={fetchUserSessions}
        />
      </DrawerMenu>
    </>
  );
};
export default UserManagement;
